import React, { useCallback, useMemo } from "react";
import { Button, Dropdown, Tooltip, Typography } from "antd";
import { MdOutlineApps, MdOutlineWeb } from "react-icons/md";
import Cookies from "js-cookie";
import { baseURL } from "../../../config/index";
import { toast } from "react-toastify";

const { Text } = Typography;

const menuItems = [
  {
    label: "CMS",
    key: "cms",
    icon: <MdOutlineWeb size={25} />,
    color: "#1890ff",
  },

  {
    label: "Carenation",
    key: "Carenation",
    icon: <MdOutlineWeb size={25} />,
    color: "#768bf5",
  },
];
const AUTH_TIMEOUT_MS = 10000; // 10 seconds
const ALLOWED_APPS = ["cms", "Carenation"];

// Utility functions to check origin
const isValidOrigin = (origin, expectedOrigin) => {
  try {
    const originUrl = new URL(origin);
    const expectedUrl = new URL(expectedOrigin);
    return originUrl.origin === expectedUrl.origin;
  } catch {
    return false;
  }
};

const createSecurePayload = (token, source) => ({
  type: "CRM_SECURE_AUTH",
  payload: {
    authToken: token,
    timestamp: Date.now(),
    context: {
      source,
      requestId: crypto.randomUUID(),
    },
  },
});

const AppsMenu = () => {
  const handleOpenApp = useCallback((key) => {
    if (!ALLOWED_APPS.includes(key)) {
      toast.error("Invalid application key");
      return;
    }

    const token = Cookies.get("token");
    if (!token) {
      toast.error("Authentication token not found.");
      return;
    }

    const source = "crm";

    if (key === "cms") {
      let authTimeoutId;
      let cmsWindow;

      try {
        // Sanitize URL
        const cmsUrl = new URL(`${baseURL.CMS_URL}/external/${source}`);

        // Open window with secure features
        cmsWindow = window.open(cmsUrl.toString(), "_blank");

        if (!cmsWindow) {
          toast.error(
            "Failed to open CMS window. Please check your pop-up blocker settings."
          );
          return;
        }

        // Message handler with proper type checking
        const messageHandler = (event) => {
          // Validate origin
          if (!isValidOrigin(event.origin, baseURL.CMS_URL)) {
            console.warn("Invalid origin:", event.origin);
            return;
          }

          // Validate data structure
          if (!event.data?.type) {
            return;
          }

          const { type, payload } = event.data;

          switch (type) {
            case "CMS_READY":
              // Create and send secure payload
              if (cmsWindow) {
                const securePayload = createSecurePayload(token, source);
                cmsWindow.postMessage(securePayload, baseURL.CMS_URL);
              }
              break;

            case "CMS_AUTH_RESULT":
              if (payload?.status === "success") {
                cleanup();
                toast.success("Successfully connected to CMS");
              } else if (payload?.status === "error") {
                cleanup();
                toast.error(payload.message || "Authentication failed");
              }
              break;

            default:
              console.warn("Unhandled message type:", type);
          }
        };

        // Cleanup function
        const cleanup = () => {
          clearTimeout(authTimeoutId);
          window.removeEventListener("message", messageHandler);
        };

        // Set authentication timeout
        authTimeoutId = setTimeout(() => {
          cleanup();
          toast.error("CMS Authentication timed out. Please try again.");
          cmsWindow?.close();
        }, AUTH_TIMEOUT_MS);

        // Add message event listener
        window.addEventListener("message", messageHandler);
      } catch (error) {
        toast.error("Failed to open CMS. Please try again later.");
        cmsWindow?.close();
      }
    }

    if (key === "Carenation") {
      let authTimeoutId;
      let carenationWindow;

      try {
        const CARENATION_URL = "https://carenation.traya.health/auth/google/app"
        // Sanitize URL
        const Url = new URL(`${CARENATION_URL}`);

        // Open window with secure features
        carenationWindow = window.open(Url.toString(), "_blank");

        if (!carenationWindow) {
          toast.error(
            "Failed to open CMS window. Please check your pop-up blocker settings."
          );
          return;
        }
        const securePayload = createSecurePayload(token, source);
                carenationWindow.postMessage(securePayload, CARENATION_URL);
      } catch (error) {
        toast.error(error.message || "Failed to open CMS. Please try again later.");
        carenationWindow?.close();
      }
    }
  }, []);

  const getItems = useMemo(
    () =>
      menuItems.map((item) => ({
        key: item.key,
        label: (
          <Tooltip title={`Open ${item.label}`} placement="left">
            <Button onClick={() => handleOpenApp(item.key)}>
              <div style={{ color: item.color }}>{item.icon}</div>
              <Text strong>{item.label}</Text>
            </Button>
          </Tooltip>
        ),
      })),
    [handleOpenApp]
  );

  return (
    <div className="mx-2">
      <Dropdown
        menu={{ items: getItems }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button
          shape="round"
          icon={<MdOutlineApps size={20} />}
          className="flex items-center px-3"
          style={{ height: "35px" }}
        >
          Apps
        </Button>
      </Dropdown>
    </div>
  );
};

export default AppsMenu;
